/**
 * Integrytas Group - Common Security Module (CSM)
 * NotificationBell — header icon with unread badge + history panel.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import type { Notification, NotificationType } from '../../lib/use_notifications';

interface NotificationBellProps {
  notifications: Notification[];
  unreadCount:   number;
  onMarkRead:    (id: number) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
}

const TYPE_ICON: Record<NotificationType, string> = {
  SUCCESS: '✓', INFO: 'ℹ', WARNING: '⚠', ERROR: '✕', SYSTEM: '⚙',
};
const TYPE_COLOR: Record<NotificationType, string> = {
  SUCCESS: 'text-csm-success',
  INFO:    'text-csm-info',
  WARNING: 'text-csm-warning',
  ERROR:   'text-csm-danger',
  SYSTEM:  'text-primary',
};

export function NotificationBell({
  notifications, unreadCount, onMarkRead, onMarkAllRead,
}: NotificationBellProps) {
  const { t }      = useTranslation();
  const [open, setOpen]   = useState(false);
  const panelRef          = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)  return t('notifications.just_now');
    if (m < 60) return t('notifications.minutes_ago', { count: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t('notifications.hours_ago', { count: h });
    return t('notifications.days_ago', { count: Math.floor(h / 24) });
  };

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={t('notifications.bell_label', { count: unreadCount })}
        title={t('notifications.bell_label', { count: unreadCount })}
        className="relative flex h-7 w-7 items-center justify-center rounded
                   text-white/80 hover:bg-white/10 hover:text-white"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center
                           justify-center rounded-full bg-csm-danger px-1
                           text-csm-2xs font-bold text-white leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 w-80 rounded border
                     border-csm-border bg-white shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-csm-border
                          px-3 py-2">
            <span className="text-csm-sm font-semibold text-csm-ink">
              {t('notifications.panel_title')}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => onMarkAllRead()}
                className="text-csm-xs text-primary hover:underline"
              >
                {t('notifications.mark_all_read')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 && (
              <div className="px-4 py-6 text-center text-csm-sm italic text-csm-muted">
                {t('notifications.empty')}
              </div>
            )}
            {notifications.map(n => (
              <button
                key={n.notification_id}
                type="button"
                onClick={() => !n.is_read && onMarkRead(n.notification_id)}
                className={[
                  'flex w-full items-start gap-2 border-b border-csm-border px-3 py-2.5',
                  'text-left hover:bg-csm-surface-alt',
                  !n.is_read ? 'bg-primary-soft' : '',
                ].join(' ')}
              >
                {/* Type icon */}
                <span className={`mt-0.5 text-csm-md ${TYPE_COLOR[n.notification_type]}`}>
                  {TYPE_ICON[n.notification_type]}
                </span>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className={`text-csm-sm leading-snug ${!n.is_read ? 'font-semibold text-csm-ink' : 'text-csm-ink-soft'}`}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="mt-0.5 text-csm-xs text-csm-muted truncate">
                      {n.body}
                    </div>
                  )}
                  <div className="mt-0.5 text-csm-2xs text-csm-muted">
                    {relativeTime(n.created_at)}
                  </div>
                </div>
                {/* Unread dot */}
                {!n.is_read && (
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-csm-border px-3 py-1.5 text-csm-xs text-csm-muted text-right">
            {t('notifications.showing_last', { count: notifications.length })}
          </div>
        </div>
      )}
    </div>
  );
}
